// src/pages/StoryboardPage.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { keepReadonly } from '../utils/embedParams';
import { useSearchParams } from 'react-router-dom';
import { useEmbedTheme } from '../hooks/useEmbedTheme';
import {
  BookOpen, StickyNote, Frame, Plus, Palette, Link2, Scissors, Copy, Trash2, Search, ChevronDown, MousePointer2, X, BringToFront, SendToBack,
} from 'lucide-react';
import {
  ReactFlow, Background, Controls, MiniMap,
  useNodesState, Node, NodeTypes, NodeChange,
  ReactFlowInstance, OnNodeDrag,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { apiGet } from '../api';
import { useStoryboard, StoryNote, StoryFrame } from '../hooks/useStoryboard';
import StickyNoteNode, { StickyNoteData } from '../components/StickyNoteNode';
import FrameNode from '../components/FrameNode';
import EntityFilterBar, { FilterEntity } from '../components/EntityFilterBar';
import EntityInfoCard from '../components/EntityInfoCard';
import { randomUUID } from '../utils/uuid';

// ── Constants ─────────────────────────────────────────────────────────────────
const NOTE_COLORS = ['#FFE066', '#B5EAD7', '#FFD6E7', '#C7CEEA', '#F9C784', '#E2E2E2'];
const FRAME_COLORS = ['#7ab3e0', '#f5c842', '#a0d4a0', '#e0a07a', '#c7ceea', '#f0a0c0'];
const DEFAULT_NOTE_COLOR = NOTE_COLORS[0];
const DEFAULT_FRAME_COLOR = FRAME_COLORS[0];
const DEFAULT_NOTE_WIDTH = 200;
const DEFAULT_FRAME_WIDTH = 300;
const DEFAULT_FRAME_HEIGHT = 200;
// A note's stacking order (#1072): where notes overlap, the higher one is in front. A note
// without one counts as 0.
const stackOf = (n: StoryNote) => n.z ?? 0;

// ── Types ─────────────────────────────────────────────────────────────────────
interface BookOption { entityId: string; title: string; }
interface EntityOption { entityId: string; name: string; type: 'CHARACTER' | 'LOCATION' | 'EVENT'; }
interface ContextMenuState { x: number; y: number; nodeId: string; nodeType: 'stickynote' | 'frame'; }

interface EntityLinkMenuState { x: number; y: number; noteId: string; }

const TYPE_LABEL_MAP: Record<string, string> = {
  CHARACTER: 'Characters', LOCATION: 'Locations', EVENT: 'Events',
};

// Stable node types — defined outside component to avoid React Flow warnings
const NODE_TYPES: NodeTypes = { stickynote: StickyNoteNode, frame: FrameNode };

function slugify(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Touch devices have no middle/right mouse button, so the desktop select-mode
// pan bindings ([1,2]) leave the canvas unpannable. Detect a coarse pointer and
// fall back to single-finger pan.
const isCoarsePointer = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

// ── Component ────────────────────────────────────────────────────────────────
const StoryboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const bookSlugParam = searchParams.get('book');
  // Desktop client sends ?bookId=BOOK#<uuid> — strip prefix to get bare UUID
  const bookIdFlag = searchParams.get('bookId')?.replace('BOOK#', '') || null;
  const isReadonly = searchParams.get('readonly') === 'true';
  const embedTheme = useEmbedTheme();

  // Books list (for picker)
  const [books, setBooks] = useState<BookOption[]>([]);
  const [booksLoading, setBooksLoading] = useState(false);

  // Maps built once books load: slug ↔ book
  const bookBySlug = useMemo(() => {
    const seen = new Map<string, number>();
    const result = new Map<string, BookOption>();
    books.forEach(b => {
      const base = slugify(b.title);
      const n = seen.get(base) ?? 0;
      seen.set(base, n + 1);
      result.set(n === 0 ? base : `${base}-${n + 1}`, b);
    });
    return result;
  }, [books]);

  const slugForBook = useMemo(() => {
    const result = new Map<string, string>();
    bookBySlug.forEach((b, slug) => result.set(b.entityId, slug));
    return result;
  }, [bookBySlug]);

  // Resolve slug or direct bookId flag → bare UUID
  const bookIdParam = useMemo(() => {
    if (bookIdFlag) return bookIdFlag;
    if (!bookSlugParam) return null;
    const book = bookBySlug.get(bookSlugParam);
    return book ? book.entityId.replace('BOOK#', '') : null;
  }, [bookIdFlag, bookSlugParam, bookBySlug]);

  // Current book resolved from the effective id — works whether we arrived via
  // ?book=<slug> or ?bookId=<uuid> (the bookshelf links use the latter).
  const currentBook = useMemo(
    () => books.find(b => b.entityId.replace('BOOK#', '') === bookIdParam) ?? null,
    [books, bookIdParam],
  );

  useEffect(() => {
    setBooksLoading(true);
    apiGet('/books')
      .then(r => r.body.json() as unknown as Promise<BookOption[]>)
      .then(setBooks)
      .catch(() => setBooks([]))
      .finally(() => setBooksLoading(false));
  }, []); // fetch once on mount

  // Storyboard data
  const { board, notes, loading, error, saveBoard, createNote, updateNote, deleteNote } = useStoryboard(bookIdParam);
  // The notes as they are now, for a timer that fires later: one that read the notes it was
  // made with would save that old copy, undoing a move, a colour or a stacking order (#1072).
  const notesRef = useRef(notes);
  useEffect(() => { notesRef.current = notes; }, [notes]);

  // Entities (for entity link search + filter bar)
  const [entities, setEntities] = useState<EntityOption[]>([]);
  useEffect(() => {
    if (!bookIdParam) return;
    apiGet(`/entities?bookId=BOOK%23${bookIdParam}`)
      .then(r => r.body.json())
      .then((data: any) => {
        const chars: EntityOption[] = (data.characters || []).map((e: any) => ({ entityId: e.entityId, name: e.name, type: 'CHARACTER' as const }));
        const locs: EntityOption[] = (data.locations  || []).map((e: any) => ({ entityId: e.entityId, name: e.name, type: 'LOCATION'  as const }));
        const evts: EntityOption[] = (data.events     || []).map((e: any) => ({ entityId: e.entityId, name: e.name, type: 'EVENT'     as const }));
        setEntities([...chars, ...locs, ...evts]);
      })
      .catch(() => setEntities([]));
  }, [bookIdParam]);

  // Entity info card
  const [focusedEntityId, setFocusedEntityId] = useState<string | null>(null);

  // Note edit panel (opens on note click — read/edit the full note comfortably)
  const [panelNoteId, setPanelNoteId] = useState<string | null>(null);
  // Local draft for the panel textarea so a debounced-save node rebuild can't
  // drop keystrokes mid-typing. Re-seeded only when a different note is opened.
  const [noteDraft, setNoteDraft] = useState('');

  // Local frames state (synced from board on load)
  const [frames, setFrames] = useState<StoryFrame[]>([]);
  useEffect(() => { setFrames(board?.frames ?? []); }, [board]);

  const boardSaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveFrames = useCallback((nextFrames: StoryFrame[]) => {
    setFrames(nextFrames);
    clearTimeout(boardSaveTimer.current);
    boardSaveTimer.current = setTimeout(() => saveBoard(nextFrames), 800);
  }, [saveBoard]);

  // Filters
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const toggleFilter = useCallback((entityId: string) => {
    setActiveFilters(prev =>
      prev.includes(entityId) ? prev.filter(id => id !== entityId) : [...prev, entityId]
    );
  }, []);
  const clearFilters = useCallback(() => setActiveFilters([]), []);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [entityLinkMenu, setEntityLinkMenu] = useState<EntityLinkMenuState | null>(null);
  const [entitySearch, setEntitySearch] = useState('');

  const closeMenus = useCallback(() => {
    setContextMenu(null);
    setEntityLinkMenu(null);
    setEntitySearch('');
  }, []);

  // React Flow state — MUST be before all callbacks that call setNodes
  const rfInstance = useRef<ReactFlowInstance | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);

  // Note text change (debounced 800ms)
  const textSaveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup pending debounce timers on unmount
  useEffect(() => {
    const timers = textSaveTimers.current;
    return () => {
      clearTimeout(boardSaveTimer.current);
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleNoteTextChange = useCallback((noteId: string, text: string) => {
    clearTimeout(textSaveTimers.current.get(noteId));
    textSaveTimers.current.set(noteId, setTimeout(() => {
      const note = notesRef.current.find(n => n.entityId === noteId);
      if (note) updateNote(noteId, { ...note, text }).catch(console.error);
    }, 800));
  }, [updateNote]);

  // Note resize end → immediate PUT
  const handleNoteResizeEnd = useCallback((noteId: string, width: number, height: number) => {
    const note = notes.find(n => n.entityId === noteId);
    if (note) updateNote(noteId, { ...note, width, height }).catch(console.error);
  }, [notes, updateNote]);

  // Frame resize end → save board with new size AND position (x/y change when resizing from top/left)
  const handleFrameResizeEnd = useCallback((frameId: string, width: number, height: number, x: number, y: number) => {
    const next = frames.map(f => f.id === frameId ? { ...f, width, height, x, y } : f);
    saveFrames(next);
  }, [frames, saveFrames]);

  // Frame label change (debounced)
  const handleFrameLabelChange = useCallback((frameId: string, label: string) => {
    setFrames(prev => {
      const next = prev.map(f => f.id === frameId ? { ...f, label } : f);
      clearTimeout(boardSaveTimer.current);
      boardSaveTimer.current = setTimeout(() => saveBoard(next), 800);
      return next;
    });
    setNodes(nds => nds.map(n =>
      n.id === frameId ? { ...n, data: { ...n.data, label } } : n
    ));
  }, [saveBoard, setNodes]);

  // Context menu handlers
  const handleNoteContextMenu = useCallback((e: React.MouseEvent, nodeId: string) => {
    setContextMenu({ x: e.clientX, y: e.clientY, nodeId, nodeType: 'stickynote' });
  }, []);

  const handleFrameContextMenu = useCallback((e: React.MouseEvent, nodeId: string) => {
    setContextMenu({ x: e.clientX, y: e.clientY, nodeId, nodeType: 'frame' });
  }, []);

  // Seed the draft only when a different note is opened (not on every save/rebuild).
  useEffect(() => {
    if (!panelNoteId) return;
    setNoteDraft(notes.find(n => n.entityId === panelNoteId)?.text ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelNoteId]);

  // Panel text edit: keep the draft, update the note live on the canvas, debounce-save.
  const handlePanelTextChange = useCallback((noteId: string, text: string) => {
    setNoteDraft(text);
    setNodes(nds => nds.map(n => n.id === noteId ? { ...n, data: { ...n.data, text } } : n));
    handleNoteTextChange(noteId, text);
  }, [setNodes, handleNoteTextChange]);

  const handleColorChange = useCallback((nodeId: string, color: string) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, color } } : n
    ));
    const note = notes.find(n => n.entityId === nodeId);
    if (note) updateNote(nodeId, { ...note, color }).catch(console.error);
    closeMenus();
  }, [notes, updateNote, closeMenus, setNodes]);

  const handleOpenEntityLink = useCallback((noteId: string) => {
    const cm = contextMenu;
    closeMenus();
    if (cm) setEntityLinkMenu({ x: cm.x, y: cm.y, noteId });
  }, [contextMenu, closeMenus]);

  const handleEntitySelect = useCallback((noteId: string, entityId: string) => {
    const entityName = entities.find(e => e.entityId === entityId)?.name ?? entityId;
    setNodes(nds => nds.map(n =>
      n.id === noteId ? { ...n, data: { ...n.data, linkedEntityId: entityId, linkedEntityName: entityName } } : n
    ));
    const note = notes.find(n => n.entityId === noteId);
    // Keep both link fields consistent so the request never carries a stale
    // linkedEntityIds alongside the new scalar.
    if (note) updateNote(noteId, { ...note, linkedEntityId: entityId, linkedEntityIds: [entityId] }).catch(console.error);
    closeMenus();
  }, [entities, notes, updateNote, closeMenus, setNodes]);

  const handleUnlinkEntity = useCallback((noteId: string) => {
    setNodes(nds => nds.map(n =>
      n.id === noteId ? { ...n, data: { ...n.data, linkedEntityId: null, linkedEntityName: null } } : n
    ));
    const note = notes.find(n => n.entityId === noteId);
    if (note) updateNote(noteId, { ...note, linkedEntityId: null, linkedEntityIds: [] }).catch(console.error);
    closeMenus();
  }, [notes, updateNote, closeMenus, setNodes]);

  const handleCopyNote = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    const note = notes.find(n => n.entityId === nodeId);
    if (!node || !note || !bookIdParam) return;
    createNote({
      bookId: `BOOK#${bookIdParam}`,
      text: note.text,
      color: note.color,
      x: node.position.x + 16,
      y: node.position.y + 16,
      width: note.width,
      height: note.height,
      linkedEntityId: note.linkedEntityId,
      z: Math.max(0, ...notes.map(stackOf)) + 1,
    }).catch(console.error);
    closeMenus();
  }, [nodes, notes, bookIdParam, createNote, closeMenus]);

  // Bring a note in front of every other note, or behind every other (#1072). Saves nothing
  // when it is already there.
  const handleStackNote = useCallback((nodeId: string, to: 'front' | 'back') => {
    closeMenus();
    const note = notes.find(n => n.entityId === nodeId);
    const others = notes.filter(n => n.entityId !== nodeId).map(stackOf);
    if (!note || others.length === 0) return;
    const own = stackOf(note);
    if (to === 'front' ? own > Math.max(...others) : own < Math.min(...others)) return;
    const z = to === 'front' ? Math.max(...others) + 1 : Math.min(...others) - 1;
    updateNote(nodeId, { ...note, z }).catch(console.error);
  }, [notes, updateNote, closeMenus]);

  const handleDeleteNode = useCallback((nodeId: string, nodeType: 'stickynote' | 'frame') => {
    if (nodeType === 'stickynote') {
      deleteNote(nodeId).catch(console.error);
      setNodes(nds => nds.filter(n => n.id !== nodeId));
    } else {
      saveFrames(frames.filter(f => f.id !== nodeId));
      setNodes(nds => nds.filter(n => n.id !== nodeId));
    }
    closeMenus();
  }, [frames, deleteNote, saveFrames, closeMenus, setNodes]);

  // Build React Flow nodes from data
  const buildNodes = useCallback((
    notesArr: StoryNote[],
    framesArr: StoryFrame[],
    filters: string[],
    entityList: EntityOption[],
    readonly: boolean,
  ): Node[] => {
    const frameNodes: Node[] = framesArr.map(f => ({
      id: f.id,
      type: 'frame',
      position: { x: f.x, y: f.y },
      style: { width: f.width, height: f.height },
      zIndex: 0,
      data: {
        label: f.label,
        color: f.color,
        isReadonly: readonly,
        onLabelChange: handleFrameLabelChange,
        onContextMenu: handleFrameContextMenu,
        onResizeEnd: handleFrameResizeEnd,
      } as unknown as Record<string, unknown>,
    }));

    // Back to front: React Flow draws later nodes over earlier ones at the same zIndex. The
    // sort is stable, so notes with the same order keep the order they came in.
    const noteNodes: Node[] = [...notesArr].sort((a, b) => stackOf(a) - stackOf(b)).map(n => ({
      id: n.entityId,
      type: 'stickynote',
      position: { x: n.x, y: n.y },
      style: { width: n.width, height: n.height || 120 },
      zIndex: 1,
      data: {
        text: n.text,
        color: n.color,
        linkedEntityId: n.linkedEntityId,
        linkedEntityName: n.linkedEntityId
          ? (entityList.find(e => e.entityId === n.linkedEntityId)?.name ?? n.linkedEntityId)
          : null,
        activeFilters: filters,
        isReadonly: readonly,
        onTextChange: handleNoteTextChange,
        onResizeEnd: handleNoteResizeEnd,
        onContextMenu: handleNoteContextMenu,
        onEntityClick: setFocusedEntityId,
      } as unknown as Record<string, unknown>,
    }));

    return [...frameNodes, ...noteNodes];
  }, [handleFrameLabelChange, handleFrameContextMenu, handleFrameResizeEnd, handleNoteTextChange, handleNoteResizeEnd, handleNoteContextMenu, setFocusedEntityId]);

  // Sync nodes when storyboard data loads
  useEffect(() => {
    if (!loading) {
      setNodes(buildNodes(notes, frames, activeFilters, entities, isReadonly));
    }
  }, [notes, frames, loading, entities, isReadonly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update activeFilters on all note nodes without re-fetching
  useEffect(() => {
    setNodes(nds => nds.map(n =>
      n.type === 'stickynote' ? { ...n, data: { ...n.data, activeFilters } } : n
    ));
  }, [activeFilters, setNodes]);

  // Drag end → save note position (immediate) or frame position (debounced)
  const handleNodeDragStop: OnNodeDrag = useCallback((_, node) => {
    if (node.type === 'stickynote') {
      const note = notes.find(n => n.entityId === node.id);
      if (note) updateNote(node.id, { ...note, x: node.position.x, y: node.position.y }).catch(console.error);
    } else if (node.type === 'frame') {
      const next = frames.map(f => f.id === node.id ? { ...f, x: node.position.x, y: node.position.y } : f);
      saveFrames(next);
    }
  }, [notes, frames, updateNote, saveFrames]);

  // Selection drag stop → batch-save positions for all dragged nodes
  const handleSelectionDragStop = useCallback((_: React.MouseEvent, draggedNodes: Node[]) => {
    if (isReadonly) return;
    draggedNodes.forEach(node => {
      if (node.type === 'stickynote') {
        const note = notes.find(n => n.entityId === node.id);
        if (note) updateNote(node.id, { ...note, x: node.position.x, y: node.position.y }).catch(console.error);
      }
    });
    const draggedFrames = draggedNodes.filter(n => n.type === 'frame');
    if (draggedFrames.length > 0) {
      const posMap = new Map(draggedFrames.map(n => [n.id, n.position]));
      saveFrames(frames.map(f => posMap.has(f.id) ? { ...f, ...posMap.get(f.id) } : f));
    }
  }, [isReadonly, notes, frames, updateNote, saveFrames]);

  // onNodesChange wrapper — intercepts Delete-key removes to batch-delete from API
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    const removes = changes.filter((c): c is NodeChange & { type: 'remove' } => c.type === 'remove');
    if (removes.length > 0 && !isReadonly) {
      const removedIds = new Set(removes.map(c => c.id));
      removes.forEach(c => {
        const node = nodes.find(n => n.id === c.id);
        if (node?.type === 'stickynote') deleteNote(c.id).catch(console.error);
      });
      const removedFrameIds = nodes.filter(n => n.type === 'frame' && removedIds.has(n.id)).map(n => n.id);
      if (removedFrameIds.length > 0) {
        const next = frames.filter(f => !removedFrameIds.includes(f.id));
        clearTimeout(boardSaveTimer.current);
        boardSaveTimer.current = setTimeout(() => saveBoard(next), 800);
        setFrames(next);
      }
    }
    onNodesChange(changes);
  }, [nodes, frames, isReadonly, onNodesChange, deleteNote, saveBoard, setFrames]);

  // Delete all selected nodes
  const handleDeleteSelected = useCallback(() => {
    const selected = nodes.filter(n => n.selected);
    if (selected.length === 0 || isReadonly) return;
    selected.forEach(n => {
      if (n.type === 'stickynote') deleteNote(n.id).catch(console.error);
    });
    const removedIds = new Set(selected.map(n => n.id));
    const nextFrames = frames.filter(f => !removedIds.has(f.id));
    if (nextFrames.length !== frames.length) saveFrames(nextFrames);
    setNodes(nds => nds.filter(n => !removedIds.has(n.id)));
  }, [nodes, frames, isReadonly, deleteNote, saveFrames, setNodes]);

  const selectedCount = nodes.filter(n => n.selected).length;
  const [selectMode, setSelectMode] = useState(false);

  // Toolbar: create note
  const handleCreateNote = useCallback(async () => {
    if (!bookIdParam) return;
    const center = rfInstance.current?.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }) ?? { x: 100, y: 100 };
    await createNote({
      bookId: `BOOK#${bookIdParam}`,
      text: '',
      color: DEFAULT_NOTE_COLOR,
      x: center.x - DEFAULT_NOTE_WIDTH / 2,
      y: center.y - 75,
      width: DEFAULT_NOTE_WIDTH,
      height: 150,
      linkedEntityId: null,
      z: Math.max(0, ...notes.map(stackOf)) + 1,
    });
  }, [bookIdParam, createNote, notes]);

  // Toolbar: create frame
  const handleCreateFrame = useCallback(() => {
    if (!bookIdParam) return;
    const center = rfInstance.current?.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }) ?? { x: 50, y: 50 };
    const newFrame: StoryFrame = {
      id: randomUUID(),
      label: 'New Section',
      color: DEFAULT_FRAME_COLOR,
      x: center.x - DEFAULT_FRAME_WIDTH / 2,
      y: center.y - DEFAULT_FRAME_HEIGHT / 2,
      width: DEFAULT_FRAME_WIDTH,
      height: DEFAULT_FRAME_HEIGHT,
    };
    saveFrames([...frames, newFrame]);
    setNodes(nds => [...nds, {
      id: newFrame.id, type: 'frame',
      position: { x: newFrame.x, y: newFrame.y },
      style: { width: newFrame.width, height: newFrame.height },
      zIndex: 0,
      data: {
        label: newFrame.label, color: newFrame.color,
        onLabelChange: handleFrameLabelChange,
        onContextMenu: handleFrameContextMenu,
        onResizeEnd: handleFrameResizeEnd,
      } as unknown as Record<string, unknown>,
    }]);
  }, [bookIdParam, frames, saveFrames, handleFrameLabelChange, handleFrameContextMenu, handleFrameResizeEnd, setNodes]);

  // Filter bar entities (derived from linked notes)
  const filterEntities = useMemo<FilterEntity[]>(() => {
    const countMap = new Map<string, number>();
    notes.forEach(n => {
      if (n.linkedEntityId) countMap.set(n.linkedEntityId, (countMap.get(n.linkedEntityId) ?? 0) + 1);
    });
    return [...countMap.entries()].map(([entityId, noteCount]) => {
      const entity = entities.find(e => e.entityId === entityId);
      return {
        entityId,
        name: entity?.name ?? entityId,
        type: entity?.type ?? 'CHARACTER',
        noteCount,
      };
    });
  }, [notes, entities]);

  // Entity search for link menu
  const filteredLinkEntities = useMemo(() =>
    entities.filter(e => !entitySearch || e.name.toLowerCase().includes(entitySearch.toLowerCase())),
    [entities, entitySearch]
  );

  // Derived for context menu
  const selectedNoteLinkedId = contextMenu
    ? (nodes.find(n => n.id === contextMenu.nodeId)?.data as unknown as StickyNoteData | undefined)?.linkedEntityId ?? null
    : null;

  // Derived for the note edit panel (closes automatically if the note is deleted)
  const panelNode = panelNoteId
    ? nodes.find(n => n.id === panelNoteId && n.type === 'stickynote')
    : undefined;
  const panelData = panelNode?.data as unknown as StickyNoteData | undefined;

  // ── Book picker ──────────────────────────────────────────────────────────
  // Show picker when no slug, or while books are still loading (slug not yet resolved)
  if (!bookIdParam && !bookIdFlag && !(bookSlugParam && booksLoading)) {
    return (
      <div className="sb-picker-wrap" data-theme={embedTheme}>
        <div className="sb-picker-card">
          <h2>Storyboard</h2>
          <p>Select a book to open its storyboard.</p>
          {booksLoading ? (
            <p className="sb-picker-empty">Loading books…</p>
          ) : books.length === 0 ? (
            <p className="sb-picker-empty">No books in your library. Create your first book with the Chaptery desktop application.</p>
          ) : (
            <ul className="sb-picker-list">
              {books.map(b => (
                <li key={b.entityId}>
                  <button
                    className="sb-picker-item"
                    onClick={() => setSearchParams(keepReadonly({ book: slugForBook.get(b.entityId) ?? slugify(b.title) }, searchParams))}
                  >
                    {b.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // ── Canvas page ──────────────────────────────────────────────────────────
  return (
    <div className={`sb-page${isReadonly ? ' readonly' : ''}`} data-theme={embedTheme}>

      {/* Toolbar */}
      <div className="sb-toolbar">
        {/* Read-only stays read-only (#1034). */}
        <button className="sb-book-btn" onClick={() => setSearchParams(keepReadonly({}, searchParams))}>
          <BookOpen size={13} />
          <span className="sb-book-btn-title">
            {currentBook?.title ?? 'Book'}
          </span>
          <ChevronDown size={12} className="sb-book-btn-caret" />
        </button>

        {!isReadonly && (<>
          <div className="sb-toolbar-divider" />
          <button className="sb-btn-add-note" onClick={handleCreateNote}>
            <Plus size={13} /><StickyNote size={13} /> Note
          </button>
          <button className="sb-btn-add-frame" onClick={handleCreateFrame}>
            <Frame size={13} /> Frame
          </button>
          <div className="sb-toolbar-divider" />
          <button
            className={`sb-btn-select-mode${selectMode ? ' active' : ''}`}
            onClick={() => setSelectMode(m => !m)}
            title={selectMode ? 'Switch to pan mode' : 'Switch to select mode'}
          >
            <MousePointer2 size={13} /> {selectMode ? 'Select' : 'Pan'}
          </button>
          {selectedCount > 1 && (
            <>
              <div className="sb-toolbar-divider" />
              <div className="sb-sel-indicator">
                <span className="sb-sel-count">{selectedCount} selected</span>
                <button className="sb-sel-delete-btn" onClick={handleDeleteSelected}>
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </>
          )}
        </>)}

        <div className="sb-toolbar-divider" />

        <EntityFilterBar
          entities={filterEntities}
          activeFilters={activeFilters}
          onToggle={toggleFilter}
          onClear={clearFilters}
        />

        {isReadonly && (() => {
          const p = new URLSearchParams(searchParams);
          p.delete('readonly');
          const qs = p.toString();
          return (
            <a
              href={`${window.location.origin}${window.location.pathname}${qs ? `?${qs}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="sb-readonly-link"
            >
              Edit ↗
            </a>
          );
        })()}
      </div>

      {loading && <div className="sb-loading">Loading storyboard…</div>}
      {error && <div className="sb-error">{error}</div>}

      {/* Canvas */}
      {!loading && !error && (
        <div className="sb-canvas-wrap" onClick={closeMenus}>
          <ReactFlow
            nodes={nodes}
            edges={[]}
            onNodesChange={handleNodesChange}
            onNodeClick={(_, node) => { if (node.type === 'stickynote') setPanelNoteId(node.id); }}
            onPaneClick={() => { setPanelNoteId(null); closeMenus(); }}
            onNodeDragStop={isReadonly ? undefined : handleNodeDragStop}
            onSelectionDragStop={isReadonly ? undefined : handleSelectionDragStop}
            nodeTypes={NODE_TYPES}
            nodesDraggable={!isReadonly}
            deleteKeyCode={isReadonly ? null : 'Delete'}
            multiSelectionKeyCode="Shift"
            selectionKeyCode="Shift"
            selectionOnDrag={!isReadonly && selectMode}
            panOnDrag={isReadonly || !selectMode || isCoarsePointer() ? true : [1, 2]}
            selectionMode={'partial' as any}
            elevateNodesOnSelect={false}
            onInit={(instance) => {
              rfInstance.current = instance as unknown as ReactFlowInstance;
              // Fit once, to what the board opened with. Not the fitView prop: that queues
              // a fit until the nodes are measured, and an empty board has none, so it
              // waited for the first note and zoomed to 200% around it alone. (#957)
              // The canvas that stays mounted is the one mounted after the board loaded, so
              // notes and board here are the loaded ones; board.frames rather than the
              // frames state, which an effect syncs a render later.
              if (notes.length > 0 || (board?.frames?.length ?? 0) > 0) {
                instance.fitView({ padding: 0.15 });
              }
            }}
            onlyRenderVisibleElements
            minZoom={0.2}
            maxZoom={2}
            style={{ background: embedTheme === 'dark' ? '#0f1e1d' : '#f0f1f3' }}
          >
            {embedTheme !== 'dark' && <Background color="#ccc" gap={20} size={1} />}
            {/* The fit button queues a fit the same way, so it only shows when there is
                something to fit. (#957) */}
            <Controls showFitView={nodes.length > 0} fitViewOptions={{ padding: 0.15 }} />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'frame') return 'rgba(154,179,212,0.15)';
                const d = node.data as { color?: string };
                return d.color ?? '#FFE066';
              }}
              nodeStrokeColor={(node) => node.type === 'frame' ? '#9ab3d4' : 'transparent'}
              nodeStrokeWidth={2}
            />
          </ReactFlow>

          {/* Context menu */}
          {!isReadonly && contextMenu && (
            <div
              className="sb-ctx-menu"
              style={{ left: contextMenu.x, top: contextMenu.y }}
              onClick={e => e.stopPropagation()}
            >
              {contextMenu.nodeType === 'stickynote' && (
                <>
                  <div className="sb-color-row">
                    <Palette size={11} style={{ color: '#555', flexShrink: 0 }} />
                    <div className="sb-color-swatches">
                      {NOTE_COLORS.map(c => (
                        <div
                          key={c}
                          className="sb-color-swatch"
                          style={{ background: c }}
                          onClick={() => handleColorChange(contextMenu.nodeId, c)}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="sb-ctx-divider" />
                  <div className="sb-ctx-row" onClick={() => handleOpenEntityLink(contextMenu.nodeId)}>
                    <Link2 size={11} /> Link entity…
                  </div>
                  {selectedNoteLinkedId && (
                    <div className="sb-ctx-row" onClick={() => handleUnlinkEntity(contextMenu.nodeId)}>
                      <Scissors size={11} /> Unlink entity
                    </div>
                  )}
                  <div className="sb-ctx-row" onClick={() => handleCopyNote(contextMenu.nodeId)}>
                    <Copy size={11} /> Copy note
                  </div>
                  <div className="sb-ctx-divider" />
                  <div className="sb-ctx-row" onClick={() => handleStackNote(contextMenu.nodeId, 'front')}>
                    <BringToFront size={11} /> Bring to front
                  </div>
                  <div className="sb-ctx-row" onClick={() => handleStackNote(contextMenu.nodeId, 'back')}>
                    <SendToBack size={11} /> Send to back
                  </div>
                  <div className="sb-ctx-divider" />
                  <div className="sb-ctx-row danger" onClick={() => handleDeleteNode(contextMenu.nodeId, 'stickynote')}>
                    <Trash2 size={11} /> Delete
                  </div>
                </>
              )}
              {contextMenu.nodeType === 'frame' && (
                <div className="sb-ctx-row danger" onClick={() => handleDeleteNode(contextMenu.nodeId, 'frame')}>
                  <Trash2 size={11} /> Delete frame
                </div>
              )}
            </div>
          )}

          {/* Entity link search popup */}
          {!isReadonly && entityLinkMenu && (
            <div
              className="sb-link-popup"
              style={{ left: entityLinkMenu.x, top: entityLinkMenu.y }}
              onClick={e => e.stopPropagation()}
            >
              <div className="sb-link-popup-label">Link entity</div>
              <div className="sb-link-search">
                <Search size={11} />
                <input
                  autoFocus
                  value={entitySearch}
                  onChange={e => setEntitySearch(e.target.value)}
                  placeholder="Search characters, places..."
                />
              </div>
              <div className="sb-link-list">
                {filteredLinkEntities.length === 0 && (
                  <div className="sb-link-empty">No entities found</div>
                )}
                {(['CHARACTER', 'LOCATION', 'EVENT'] as const).map(type => {
                  const items = filteredLinkEntities.filter(e => e.type === type);
                  if (items.length === 0) return null;
                  return (
                    <div key={type}>
                      <div className="sb-link-group-label">{TYPE_LABEL_MAP[type]}</div>
                      {items.map(e => (
                        <div
                          key={e.entityId}
                          className="sb-link-item"
                          onClick={() => handleEntitySelect(entityLinkMenu.noteId, e.entityId)}
                        >
                          {e.name}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {focusedEntityId && (
        <EntityInfoCard
          entityId={focusedEntityId}
          entities={entities}
          bookId={bookIdParam}
          readonly={isReadonly}
          onClose={() => setFocusedEntityId(null)}
        />
      )}

      {panelNode && panelData && (
        <aside className="sb-note-panel" data-theme={embedTheme} onClick={e => e.stopPropagation()}>
          <header className="sb-note-panel-head">
            <span className="sb-note-panel-title"><StickyNote size={14} /> Note</span>
            <button className="sb-note-panel-close" onClick={() => setPanelNoteId(null)} aria-label="Close note panel">
              <X size={16} />
            </button>
          </header>

          <div className="sb-note-panel-body">
            {!isReadonly && (
              <div className="sb-note-panel-section">
                <span className="sb-note-panel-label"><Palette size={12} /> Colour</span>
                <div className="sb-note-panel-swatches">
                  {NOTE_COLORS.map(c => (
                    <button
                      key={c}
                      className={`sb-note-panel-swatch${panelData.color === c ? ' active' : ''}`}
                      style={{ background: c }}
                      onClick={() => handleColorChange(panelNode.id, c)}
                      aria-label={`Set colour ${c}`}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="sb-note-panel-section sb-note-panel-textwrap">
              <span className="sb-note-panel-label">Text</span>
              <textarea
                className="sb-note-panel-text"
                value={noteDraft}
                readOnly={isReadonly}
                placeholder="Write your note…"
                onChange={e => handlePanelTextChange(panelNode.id, e.target.value)}
              />
            </div>

            <div className="sb-note-panel-section">
              <span className="sb-note-panel-label"><Link2 size={12} /> Linked entity</span>
              {panelData.linkedEntityId ? (
                <div className="sb-note-panel-linked">
                  <span className="sb-note-panel-chip">{panelData.linkedEntityName ?? panelData.linkedEntityId}</span>
                  {!isReadonly && (
                    <button className="sb-note-panel-unlink" onClick={() => handleUnlinkEntity(panelNode.id)}>
                      <Scissors size={11} /> Unlink
                    </button>
                  )}
                </div>
              ) : (
                !isReadonly && (
                  <select
                    className="sb-note-panel-select"
                    value=""
                    onChange={e => { if (e.target.value) handleEntitySelect(panelNode.id, e.target.value); }}
                  >
                    <option value="">Link to an entity…</option>
                    {entities.map(en => (
                      <option key={en.entityId} value={en.entityId}>{en.name}</option>
                    ))}
                  </select>
                )
              )}
            </div>
          </div>

          {!isReadonly && (
            <footer className="sb-note-panel-foot">
              <button
                className="sb-note-panel-delete"
                onClick={() => { handleDeleteNode(panelNode.id, 'stickynote'); setPanelNoteId(null); }}
              >
                <Trash2 size={13} /> Delete note
              </button>
            </footer>
          )}
        </aside>
      )}
    </div>
  );
};

export default StoryboardPage;
